/**
 * Gameplay state management hook.
 * Handles the state machine for puzzle interaction.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { createEngine } from '@daily-puzzle/rules';
import type { DailyResponse, AttemptResponse } from '@daily-puzzle/shared';
import { AttemptResult, Status } from '@daily-puzzle/shared';
import { ApiError, submitAttempt, revealPuzzle } from '../api/client';
import { useApp } from '../context/AppContext';
import { trackAttemptMade, trackPuzzleSolved, trackPuzzleMissed, trackSolutionRevealed } from '../lib/analytics';

export type GameState =
    | { type: 'loading' }
    | { type: 'error'; message: string }
    | { type: 'ready'; fen: string }
    | { type: 'animating' }
    | { type: 'finished' };

function isSameGameState(a: GameState, b: GameState): boolean {
    if (a.type !== b.type) return false;
    if (a.type === 'ready' && b.type === 'ready') return a.fen === b.fen;
    if (a.type === 'error' && b.type === 'error') return a.message === b.message;
    return true;
}

export interface UseGameStateResult {
    gameState: GameState;
    fen: string;
    lastMove: { from: string; to: string } | null;
    legalMoves: Map<string, string[]>;
    handleMove: (from: string, to: string, promotion?: string) => void;
    handleReveal: () => void;
    toast: { message: string; isError?: boolean } | null;
    clearToast: () => void;
}

export function useGameState(
    dailyData: DailyResponse | null,
    onStatusChange?: (status: Status, attemptsUsed: number, stepIndex: number, streak?: number, solveTimeSeconds?: number) => void
): UseGameStateResult {
    const { triggerRefresh } = useApp();
    const [fen, setFen] = useState<string>('');
    const [stepIndex, setStepIndex] = useState(0);
    const [attemptsUsed, setAttemptsUsed] = useState(0);
    const [status, setStatus] = useState<Status>(Status.InProgress);
    const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null);
    const [gameState, setGameState] = useState<GameState>({ type: 'loading' });
    const [toast, setToast] = useState<{ message: string; isError?: boolean } | null>(null);

    // Initialize from daily data
    useEffect(() => {
        if (dailyData) {
            setFen(computeCurrentFen(dailyData));
            setStepIndex(dailyData.progress.step_index);
            setAttemptsUsed(dailyData.progress.attempts_used);
            setStatus(dailyData.progress.status as Status);

            // Functional update with a bail-out: callers may pass a fresh
            // dailyData reference on every render (e.g. in tests). Without
            // this guard, recreating { type: 'ready', fen } each time would
            // schedule a re-render even when the values are unchanged,
            // which combined with a fresh dailyData reference can loop.
            const next: GameState = dailyData.progress.status === 'in_progress'
                ? { type: 'ready', fen: dailyData.puzzle.start_fen }
                : { type: 'finished' };
            setGameState(prev => isSameGameState(prev, next) ? prev : next);
        }
    }, [dailyData]);

    // Clear toast after delay
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 2000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    const handleAttemptResponse = useCallback(async (response: AttemptResponse, from: string, to: string, moveSan: string) => {
        switch (response.result) {
            case AttemptResult.Illegal:
                setToast({ message: 'Illegal move', isError: true });
                setGameState({ type: 'ready', fen: response.fen });
                break;

            case AttemptResult.Incorrect:
                setToast({ message: `${moveSan} is incorrect`, isError: true });
                setFen(response.fen);
                setStepIndex(response.step_index);
                setAttemptsUsed(response.attempts_used);
                setLastMove(null);
                setGameState({ type: 'ready', fen: response.fen });
                onStatusChange?.(response.status, response.attempts_used, response.step_index);
                if (dailyData) trackAttemptMade(dailyData.date, `${from}${to}`, response.attempts_used, response.step_index, false);
                break;

            case AttemptResult.Correct:
                // If there's an auto-reply, animate it with a delay
                if (response.auto_reply_uci) {
                    const autoFrom = response.auto_reply_uci.slice(0, 2);
                    const autoTo = response.auto_reply_uci.slice(2, 4);

                    // Wait for user's move to settle, then animate opponent's reply
                    await new Promise(resolve => setTimeout(resolve, 280));

                    setFen(response.fen);
                    setLastMove({ from: autoFrom, to: autoTo });
                } else {
                    setFen(response.fen);
                    setLastMove({ from, to });
                }
                setStepIndex(response.step_index);
                setGameState({ type: 'ready', fen: response.fen });
                onStatusChange?.(response.status, response.attempts_used, response.step_index);
                if (dailyData) trackAttemptMade(dailyData.date, `${from}${to}`, response.attempts_used, response.step_index, true);
                break;

            case AttemptResult.Solved:
                // Wait for piece to land and user to register it (1.5s pause to grok)
                await new Promise(resolve => setTimeout(resolve, 1500));

                // Final move - no auto-reply, just show user's move
                setFen(response.fen);
                setStepIndex(response.step_index);
                setStatus(Status.Solved);
                setLastMove({ from, to });
                setGameState({ type: 'finished' });
                // Pass streak and solve time from attempt response when solved
                onStatusChange?.(response.status, response.attempts_used, response.step_index, response.current_streak, response.solve_time_seconds);
                if (dailyData) trackPuzzleSolved(dailyData.date, response.attempts_used, dailyData.puzzle.steps.length, response.solve_time_seconds);
                break;

            case AttemptResult.Missed:
                setFen(response.fen);
                setAttemptsUsed(response.attempts_used);
                setStatus(Status.Missed);
                setGameState({ type: 'finished' });
                onStatusChange?.(response.status, response.attempts_used, response.step_index);
                if (dailyData) trackPuzzleMissed(dailyData.date, response.attempts_used, response.step_index, dailyData.puzzle.steps.length);
                break;
        }
    }, [dailyData, onStatusChange]);

    const handleMove = useCallback(async (from: string, to: string, promotion?: string) => {
        if (!dailyData || gameState.type !== 'ready') return;

        const uci = promotion ? `${from}${to}${promotion}` : `${from}${to}`;
        const previousFen = fen;
        const previousLastMove = lastMove;

        // Local legality check
        const engine = createEngine(fen);
        let san: string | null = null;
        let isLegal = false;

        if (typeof engine.getSAN === 'function') {
            san = engine.getSAN(uci);
            isLegal = !!san;
        } else {
            // Fallback for stale rules package
            isLegal = engine.isLegalMove(uci);
            san = isLegal ? uci : null;
        }

        if (!isLegal || !san) {
            setToast({ message: 'Illegal move', isError: true });
            return;
        }

        // Optimistically update FEN so piece animates immediately
        engine.makeMove(uci);
        setFen(engine.fen());
        setLastMove({ from, to });

        setGameState({ type: 'animating' });

        try {
            const idempotencyKey = crypto.randomUUID();
            const response = await submitAttempt(dailyData.date, uci, idempotencyKey);

            handleAttemptResponse(response, from, to, san);
        } catch (error) {
            setFen(previousFen);
            setLastMove(previousLastMove);

            if (isDayRolloverError(error)) {
                setToast({ message: 'Loading current puzzle...' });
                setGameState({ type: 'loading' });
                triggerRefresh();
                return;
            }

            setToast({ message: 'Not saved', isError: true });
            setGameState({ type: 'ready', fen: previousFen });
        }
    }, [dailyData, fen, gameState, handleAttemptResponse, lastMove, triggerRefresh]);

    const handleReveal = useCallback(async () => {
        if (!dailyData) return;

        setGameState({ type: 'animating' });

        try {
            const idempotencyKey = crypto.randomUUID();
            const response = await revealPuzzle(dailyData.date, idempotencyKey);

            // Animate solution playback
            const engine = createEngine(response.fen);
            setFen(response.fen);

            for (let i = 0; i < response.solution_uci.length; i++) {
                const move = response.solution_uci[i]!;
                await new Promise(resolve => setTimeout(resolve, 800));

                engine.makeMove(move);
                setFen(engine.fen());
                setLastMove({ from: move.slice(0, 2), to: move.slice(2, 4) });
            }

            // Let user see final position for 2 seconds before showing modal
            await new Promise(resolve => setTimeout(resolve, 2000));

            setStatus(response.status);
            setGameState({ type: 'finished' });
            onStatusChange?.(response.status, attemptsUsed, stepIndex);
            if (dailyData && response.status === Status.Revealed) {
                trackSolutionRevealed(dailyData.date);
            }
        } catch (error) {
            if (isDayRolloverError(error)) {
                setToast({ message: 'Loading current puzzle...' });
                setGameState({ type: 'loading' });
                triggerRefresh();
                return;
            }
            setToast({ message: 'Failed to reveal', isError: true });
            setGameState({ type: 'ready', fen });
        }
    }, [dailyData, fen, attemptsUsed, stepIndex, onStatusChange, triggerRefresh]);

    const clearToast = useCallback(() => setToast(null), []);

    // Compute legal moves from current FEN for the Board to display hints
    // Map from source square to array of target squares (with optional promotion suffix)
    const legalMoves = useMemo(() => {
        const movesMap = new Map<string, string[]>();
        if (!fen || status !== Status.InProgress) return movesMap;

        const engine = createEngine(fen);
        const allMoves = engine.getAllLegalMoves();

        for (const move of allMoves) {
            const from = move.slice(0, 2);
            // Store the full target (e.g., "e4" or "e8q" for promotion)
            const target = move.slice(2);
            const existing = movesMap.get(from) || [];
            existing.push(target);
            movesMap.set(from, existing);
        }

        return movesMap;
    }, [fen, status]);

    return {
        gameState,
        fen,
        lastMove,
        legalMoves,
        handleMove,
        handleReveal,
        toast,
        clearToast,
    };
}

function isDayRolloverError(error: unknown): boolean {
    return error instanceof ApiError && (
        error.code === 'read_only_day' || error.code === 'future_date'
    );
}

/**
 * Compute current FEN based on progress through puzzle.
 */
function computeCurrentFen(dailyData: DailyResponse): string {
    const { puzzle, progress } = dailyData;

    if (progress.step_index === 0) {
        return puzzle.start_fen;
    }

    const engine = createEngine(puzzle.start_fen);

    for (let i = 0; i < progress.step_index; i++) {
        const step = puzzle.steps[i];
        if (!step) break;

        engine.makeMove(step.your_moves_uci[0]!);
        if (step.auto_reply_uci) {
            engine.makeMove(step.auto_reply_uci);
        }
    }

    return engine.fen();
}
